import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  SelectChangeEvent,
  Stack,
} from "@mui/material"
import { useTranslation } from "react-i18next"

import { useResortParam } from "@/hooks/useSkipassFlow"
import { usePriceSummary } from "@/queries/hooks"

import { PriceTable } from "./components"

const PriceSummaryView = ({
  selectedDate,
  selectedPassTypeId,
  setSelectedPassTypeId,
}: {
  selectedDate: Date
  selectedPassTypeId: string
  setSelectedPassTypeId: (passTypeId: string) => void
}) => {
  const { t } = useTranslation()

  const { resortExternalId } = useResortParam()

  const { data, isLoading } = usePriceSummary(resortExternalId, selectedDate)

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    )
  }
  if (!data)
    return (
      <Alert severity="error">{t("priceSummaryView.priceFetchError")}</Alert>
    )

  const tables = data.tables

  if (!tables || tables.length === 0) {
    return (
      <Alert severity="error">{t("priceSummaryView.noPricesAvailable")}</Alert>
    )
  }

  const passTypes = tables.map((t) => t.passType)

  const activePassTypeId = selectedPassTypeId || passTypes[0].id

  const selectedTable =
    tables.find((t) => t.passType.id === selectedPassTypeId) ?? tables[0]

  const hasAnySameDayPrice = selectedTable.rows.some((row) =>
    row.prices.some((cell) => cell.sameDayPrice !== null)
  )

  const handlePassTypeChange = (event: SelectChangeEvent<string>) => {
    setSelectedPassTypeId(event.target.value)
  }

  return (
    <Stack spacing={3}>
      <FormControl fullWidth sx={{ maxWidth: "357px" }}>
        <InputLabel id="pass-type-select-label">
          {t("priceSummaryView.passSelectLabel")}
        </InputLabel>
        <Select
          labelId="pass-type-select-label"
          id="pass-type-select"
          value={activePassTypeId}
          label={t("priceSummaryView.passSelectLabel")}
          onChange={handlePassTypeChange}
          MenuProps={{
            disableScrollLock: true,
          }}
          renderValue={(value) =>
            passTypes?.find((passType) => passType.id === value)?.name
          }
        >
          {passTypes.map((passType) => (
            <MenuItem key={passType.id} value={passType.id}>
              <Radio
                checked={activePassTypeId === passType.id}
                aria-hidden="true"
              />
              {passType.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <PriceTable
        rowsTitle={t("priceSummaryView.length")}
        currency={data.currency}
        dataTitle={{
          primary: data.currency,
          secondary: hasAnySameDayPrice
            ? `(${t("priceSummaryView.buySameDay")})`
            : undefined,
        }}
        table={selectedTable}
      />
    </Stack>
  )
}

export default PriceSummaryView
